// @givmo/fundraisers-module — the organization contact offer: the org grain of the §317(a) offer
// (FCA-BE12, D-FCM-15), built once. The module's draft editor (FCM-WEB6) and the employer portal's
// wizard and manage page (EP-WEB15) render this same control, the way the portal renders
// `RecipientPicker`; each host keeps its own state, lifecycle and write.
//
// It says, in order (the approved round-3b frames):
//   1. "What charities see", and one sentence stating the organization's name as a fact — the name is
//      never hideable, so no control sits on it;
//   2. the choice: a switch, OFF by default, that covers the contact email only;
//   3. ONLY while it is on, the address: a dependent row under a leading rule (never a fill),
//      prefilled and checked against the organization's exact email domain (Jonathan's Q1 ruling).
//      While it is off there is nothing to check, and nothing here can hold back a Publish or a Save.
// The switch is never disabled because of the address.

import { useId, useState, type Ref } from 'react';
import { ErrorMessage, Switch, TextField, cn, testIdProp } from '@givmo/design-system';
import {
  ORG_CONTACT_EMAIL_MAX_LENGTH,
  ORG_CONTACT_OFFER_COPY,
  orgContactProblem,
  type OrgContactRule,
} from './org-contact-offer-decode';

export interface OrgContactOfferProps {
  /** The organization's name exactly as its fundraiser page shows it. */
  orgName: string;
  rule: OrgContactRule;
  shared: boolean;
  email: string;
  onSharedChange: (shared: boolean) => void;
  onEmailChange: (email: string) => void;
  /** The server's refusal of the host's last write, shown where the person can act on it. */
  serverError?: string | null;
  /** Show the address's problem now rather than after a blur (a host that checks on submit). */
  showErrors?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  disabled?: boolean;
  /**
   * `inline` renders the heading and the notice above the choice; `none` leaves both to the host (a
   * card that wears them as its own title and subtitle).
   */
  lead?: 'inline' | 'none';
  /** The inline heading's level, under the host's own outline. */
  headingLevel?: 3 | 4;
  testIdPrefix: string;
  className?: string;
}

export function OrgContactOffer({
  orgName,
  rule,
  shared,
  email,
  onSharedChange,
  onEmailChange,
  serverError = null,
  showErrors = false,
  inputRef,
  disabled = false,
  lead = 'inline',
  headingLevel = 4,
  testIdPrefix,
  className,
}: OrgContactOfferProps) {
  const copy = ORG_CONTACT_OFFER_COPY;
  const helperId = useId();
  const [touched, setTouched] = useState(false);
  const problem = orgContactProblem(shared, email, rule);
  const shown = serverError ?? ((touched || showErrors) && problem !== null ? problem : null);
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  return (
    <div
      className={cn('flex flex-col gap-16', className)}
      {...testIdProp(`${testIdPrefix}-contact-offer`)}
    >
      {lead === 'inline' && (
        <div>
          <Heading className="text-s14 font-w600 text-text-strong">{copy.heading}</Heading>
          <p
            className="mt-4 text-pretty text-s13 text-text-secondary"
            {...testIdProp(`${testIdPrefix}-contact-notice`)}
          >
            {copy.notice(orgName)}
          </p>
        </div>
      )}
      {/* The switch with its title alone: no explainer under it (D-GRP-76). Centred as the approved
          frames show it; below `lg` the title may wrap, so the switch stays level with its first
          line. */}
      <div className="flex items-center justify-between gap-16 max-lg:items-start">
        <div className="min-w-0">
          <p className="text-s14 font-w600 text-text-strong">{copy.title}</p>
        </div>
        <Switch
          appearance="saas"
          aria-label={copy.title}
          checked={shared}
          onCheckedChange={onSharedChange}
          disabled={disabled}
          // Below `lg` the 36×20 switch keeps a 44×44 hit area (the module stylesheet's recipe,
          // carried here so a host outside `.fundraisers-module` gets it too).
          className="shrink-0 max-lg:mt-2 max-lg:after:absolute max-lg:after:-inset-x-4 max-lg:after:-inset-y-12"
          testId={`${testIdPrefix}-contact-share`}
        />
      </div>
      {shared ? (
        // The address depends on the switch, so it hangs under a leading rule (never a fill).
        <div
          className="border-l-2 border-border-strong pl-16"
          {...testIdProp(`${testIdPrefix}-contact-email-row`)}
        >
          <TextField
            ref={inputRef}
            label={copy.field}
            type="email"
            inputMode="email"
            autoComplete="email"
            spellCheck={false}
            maxLength={ORG_CONTACT_EMAIL_MAX_LENGTH}
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            onBlur={() => setTouched(true)}
            disabled={disabled}
            error={shown !== null}
            errorMessage={shown ?? undefined}
            testId={`${testIdPrefix}-contact-email`}
            {...(shown === null ? { 'aria-describedby': helperId } : {})}
          />
          {shown === null && (
            <p id={helperId} className="pt-8 text-s12 text-text-tertiary">
              {copy.helper(rule.domain)}
            </p>
          )}
        </div>
      ) : (
        // With the address hidden, a refusal of the choice itself still has somewhere to be read.
        serverError !== null && (
          <ErrorMessage className="pt-0" testId={`${testIdPrefix}-contact-error`}>
            {serverError}
          </ErrorMessage>
        )
      )}
    </div>
  );
}
